import { map } from 'lodash';
import { AppThunk } from '../../../store/rootReducer';
import { ecgIdSelector } from '../../ecg/selectors/ecg.selectors';
import { filterOptionSelector } from '../../layout/selectors/layout.selectors';
import * as clusterActions from '../actions/cluster.actions';
import { fetchClusters, reclassify } from '../client/cluster.client';
import { getServerCode } from '../client/helpers';
import { Cluster, ClusterType } from '../clustering.types';
import {
  baseClusterIdsSelector,
  baseClustersSelector,
  selectedClusterIdsSelector,
} from '../selectors/cluster.selectors';
import { selectNewCluster } from './select-new-cluster.thunk';
import { displayNotification } from '../../../shared/notification/display-notification';

export const reclassifyClusters =
  (clusterType: ClusterType, clusters: Cluster[]): AppThunk =>
  async (dispatch, getState) => {
    const hasSameCategory = clusters.some(
      (cluster) => cluster.classification === clusterType,
    );

    const filterOption = filterOptionSelector(getState());
    const clusterIds = baseClusterIdsSelector(getState());
    const selectedIds = selectedClusterIdsSelector(getState());
    const baseClusters = baseClustersSelector(getState());

    if (hasSameCategory) {
      displayNotification({
        title: 'Warning',
        description: `You cannot reclassify clusters that are already classified as ${clusterType}`,
        status: 'warning',
      });
    }

    const ecgId = ecgIdSelector(getState());
    const serverCode = getServerCode(clusterType);
    if (ecgId == null) {
      return null;
    }

    const ids = map(clusters, 'id');
    const reclassifiedIds = await reclassify(ecgId, ids, serverCode);
    if (reclassifiedIds == null) {
      return;
    }

    const serverClustersWithBeats = await fetchClusters(ecgId);
    if (serverClustersWithBeats == null) {
      return;
    }

    const clustersByBeatId = clusters.reduce(
      (acc, cluster) => ({ ...acc, [cluster.beats[0].id]: cluster }),
      {} as Record<number, Cluster>,
    );
    const reclassifiedIdSet = new Set(reclassifiedIds);
    const filteredServerClusterWithBeats = serverClustersWithBeats.filter(
      (serverCluster) => reclassifiedIdSet.has(serverCluster.id),
    );

    const newClusters: Cluster[] = filteredServerClusterWithBeats.map(
      (filteredCluster) => {
        const firstOnsetOffsetId = filteredCluster.onsets_offsets[0][2];
        const cluster = clustersByBeatId[firstOnsetOffsetId];
        const newId = filteredCluster.id;

        return {
          ...cluster,
          id: newId,
          classification: clusterType,
        };
      },
    );

    dispatch(
      clusterActions.replaceClusters({
        oldClusters: clusters,
        newClusters: newClusters,
      }),
    );

    if (filterOption !== 'All') {
      const id = clusterIds.find((id) => !selectedIds.has(id));
      let selectedCluster = null;
      if (clusterIds.length !== 0 && id != null) {
        selectedCluster = baseClusters.find((cluster) => cluster.id === id);
      }
      if (selectedCluster != null) {
        dispatch(selectNewCluster(selectedCluster));
      } else {
        dispatch(clusterActions.selectClusters({ clusterIds: new Set() }));
      }
    }
  };
